import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'
import { checklistReservaService, mensagemErroChecklistReserva } from '@/services/checklistReservaService'
import type { DecisaoItemChecklistResponse } from '@/types/checklist'
import type { ItemChecklistReserva } from '@/types/estruturacao'
import { rotuloDoItem } from '@/utils/checklistReserva'

const MAX_OBSERVACAO = 500

interface Props {
  open: boolean
  reservaId: string
  item: ItemChecklistReserva | null
  onFechar: () => void
  /** Decisão gravada (o pai relê o checklist). */
  onValidado: (decisao: DecisaoItemChecklistResponse) => void
}

/**
 * Validar um item do checklist (back-office, AN-PP3 §7.1). A decisão cobre o CONJUNTO ATIVO inteiro do
 * item — o backend exige o conjunto exato, então a tela só MOSTRA os arquivos (sem seleção; revisão PP3,
 * achado 10). O conjunto visto vai no corpo; se mudou nesse meio-tempo o backend devolve 409
 * `CHECKLIST_CONJUNTO_DESATUALIZADO`. Quando o item tem validade, pede a data de emissão. O "válido
 * até" é calculado no backend (`dataEmissao + validadeDias`) e volta na decisão — a tela não faz
 * a conta. Erros 409/422 aparecem com a mensagem do backend.
 */
export const ValidarItemDialog: FC<Props> = ({ open, reservaId, item, onFechar, onValidado }) => {
  const documentos = item?.documentos ?? []
  const [dataEmissao, setDataEmissao] = useState('')
  const [observacao, setObservacao] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setDataEmissao('')
      setObservacao('')
      setErro(null)
    }
  }, [open, item])

  if (!item) return null
  const temValidade = item.validadeDias != null
  const rotulo = rotuloDoItem(item)
  const podeSalvar =
    !salvando && documentos.length > 0 && (!temValidade || !!dataEmissao) && observacao.length <= MAX_OBSERVACAO

  const salvar = async () => {
    if (!item.chave || !podeSalvar) return
    setSalvando(true)
    setErro(null)
    try {
      const decisao = await checklistReservaService.validar(reservaId, item.chave, {
        documentos: documentos.map((d) => ({ documentoId: d.documentoId, versao: d.versao })),
        dataEmissao: temValidade ? dataEmissao : null,
        observacao: observacao.trim() || null,
      })
      onValidado(decisao)
    } catch (e) {
      setErro(mensagemErroChecklistReserva(e, 'Não foi possível validar o documento.'))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={open} onClose={salvando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Validar: {rotulo}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          A validação cobre todos os arquivos atuais do item. Se o consultor trocar um arquivo depois, a
          validação deixa de valer para o novo conjunto.
        </Typography>
        {documentos.length === 0 ? (
          <Alert severity="warning">Este item não tem arquivo processado para validar.</Alert>
        ) : (
          <List dense aria-label="Arquivos que serão validados">
            {documentos.map((d) => (
              <ListItem key={d.documentoId} disableGutters>
                <ListItemText primary={`${d.nome} (versão ${d.versao})`} />
              </ListItem>
            ))}
          </List>
        )}
        {temValidade && (
          <TextField
            label="Data de emissão do documento"
            type="date"
            value={dataEmissao}
            onChange={(e) => setDataEmissao(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            fullWidth
            sx={{ mt: 2 }}
            disabled={salvando}
            helperText={`Este documento vale ${item.validadeDias} dias a partir da emissão — a Originei calcula o "válido até" ao validar.`}
          />
        )}
        <TextField
          label="Observação (opcional)"
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
          disabled={salvando}
          error={observacao.length > MAX_OBSERVACAO}
          helperText={`${observacao.length}/${MAX_OBSERVACAO}`}
        />
        {erro && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {erro}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={() => void salvar()}
          disabled={!podeSalvar}
          startIcon={salvando ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Validar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ValidarItemDialog
